import React from 'react';
import { Box, Button, Dialog, DialogContent, Typography } from '@mui/material';
import { FileText, Eye } from '@phosphor-icons/react';

interface PdfOptionsModalProps {
    open: boolean;
    onClose: () => void;
    onExtract: () => void;
    onView: () => void;
    fileName: string;
    fileType?: 'pdf' | 'text';
}

export const PdfOptionsModal: React.FC<PdfOptionsModalProps> = ({
    open,
    onClose,
    onExtract,
    onView,
    fileName,
    fileType = 'pdf',
}) => {
    const title = fileType === 'pdf' ? 'PDF Options' : 'File Options';
    const extractLabel = fileType === 'pdf' ? 'Extract to Text' : 'Extract';
    const extractDescription = fileType === 'pdf'
        ? 'Extract text content from PDF'
        : 'Extract and store file content';
    const viewDescription = fileType === 'pdf'
        ? 'Display PDF in Media Box'
        : 'Display file in Media Box';

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    padding: 2,
                },
            }}
        >
            <DialogContent>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                        {title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                        {fileName}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Button
                        variant="outlined"
                        fullWidth
                        onClick={onExtract}
                        sx={{
                            py: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            borderColor: 'rgba(128, 128, 128, 0.3)',
                            '&:hover': {
                                borderColor: 'rgba(128, 128, 128, 0.5)',
                                backgroundColor: 'rgba(128, 128, 128, 0.05)',
                            },
                        }}
                    >
                        <FileText size={32} weight="regular" />
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {extractLabel}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {extractDescription}
                        </Typography>
                    </Button>

                    <Button
                        variant="outlined"
                        fullWidth
                        onClick={onView}
                        sx={{
                            py: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            borderColor: 'rgba(128, 128, 128, 0.3)',
                            '&:hover': {
                                borderColor: 'rgba(128, 128, 128, 0.5)',
                                backgroundColor: 'rgba(128, 128, 128, 0.05)',
                            },
                        }}
                    >
                        <Eye size={32} weight="regular" />
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            View
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {viewDescription}
                        </Typography>
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

